// Navigation items - ordered funnel structure
export const navItems = [
  { name: "Home", link: "/" },
  { name: "Services", link: "/services" },
  { name: "Tech Lab", link: "/tech-lab" },
  { name: "Manifiesto", link: "/manifiesto" },
  { name: "Contact", link: "/contact" }, 
];

// Core pillars of Holistika methodology
export const methodologyPillars = [
  {
    id: "foresight",
    title: "Foresight",
    subtitle: "Reading the environment",
    description: "Every strategy needs context. We study your market, track signals others miss, and synthesize intelligence from multiple sources. The goal: give you a clearer picture of where you are and where things are heading.",
    capabilities: [
      "Market analysis and competitive positioning",
      "Signal monitoring and scenario planning",
      "Risk mapping and opportunity identification",
      "Intelligence synthesis across sources"
    ],
    color: "amber",
    icon: "telescope"
  },
  {
    id: "business-engineering",
    title: "Business Engineering",
    subtitle: "Designing the structure",
    description: "Strategy without structure stays abstract. We translate your direction into concrete capabilities, define how value flows through your organization, and ensure every part works toward the same objectives.",
    capabilities: [
      "Capability mapping and gap analysis",
      "Value chain design and validation",
      "Organizational architecture",
      "Strategic alignment systems"
    ],
    color: "teal",
    icon: "layers"
  },
  {
    id: "process-engineering",
    title: "Process Engineering",
    subtitle: "Building the engine",
    description: "Execution happens at the process level. We design workflows tailored to your reality, document them clearly, train your teams, and continuously improve them. No generic templates—every process fits how you actually work.",
    capabilities: [
      "Custom process design and documentation",
      "Workflow automation where it makes sense",
      "Team enablement and knowledge transfer",
      "Continuous measurement and refinement"
    ],
    color: "slate",
    icon: "workflow"
  }
];

// Service offerings mapped to business areas
export const serviceAreas = [
  {
    id: "strategy",
    title: "Strategy & Direction",
    description: "Open-ended objectives born from your aspirations and capabilities, establishing a definitive vector toward success.",
    services: ["Corporate Strategy", "Market Positioning", "Growth Planning", "M&A Advisory"],
    entity: "holistika"
  },
  {
    id: "operations",
    title: "Operations Excellence",
    description: "Tactical project management and operational frameworks that translate strategy into executable programs.",
    services: ["Project Management Office", "Process Optimization", "Change Management", "Quality Assurance"],
    entity: "think-big"
  },
  {
    id: "data",
    title: "Data Governance",
    description: "Structure that generates intelligence and proposes strategies on how to use it, adapting to your unique needs.",
    services: ["Data Strategy", "Business Intelligence", "Reporting Architecture", "Analytics Implementation"],
    entity: "tech-lab"
  },
  {
    id: "marketing",
    title: "Marketing Operations",
    description: "Channel strategy and customer experience design backed by data-driven decision making.",
    services: ["Digital Marketing", "Brand Strategy", "Customer Journey Design", "Growth Hacking"],
    entity: "think-big"
  },
  {
    id: "technology",
    title: "Technology Solutions",
    description: "AI-powered platforms and infrastructure that enable your business logic at scale.",
    services: ["AI Integration", "Platform Development", "API Architecture", "Security Implementation"],
    entity: "tech-lab"
  },
  {
    id: "people",
    title: "People & Organization",
    description: "Talent development and organizational design that builds high-performing, aligned teams.",
    services: ["Organizational Design", "Talent Development", "Culture Transformation", "Leadership Coaching"],
    entity: "think-big"
  }
];

// Three services of Holistika Group
export const entities = {
  holistika: {
    name: "Holistika",
    tagline: "Strategy & Research",
    description: "We develop proprietary frameworks and conduct deep market research to inform your strategic direction with actionable intelligence.",
    color: "amber"
  },
  "think-big": {
    name: "Think Big",
    tagline: "Consulting & Execution",
    description: "We implement transformation programs using battle-tested processes that deliver measurable operational improvements.",
    color: "teal"
  },
  "tech-lab": {
    name: "HLK Tech Lab",
    tagline: "Technology & Platforms",
    description: "We build and deploy intelligent automation systems that scale your business logic and reduce operational complexity.",
    color: "slate"
  }
};

// Approach phases for services
export const approachPhases = [
  {
    phase: 1,
    title: "Discovery & Strategy",
    description: "We analyze your objectives in their most ambitious form, understanding the search for improvement in capacity and value. This phase establishes the vision and direction.",
    deliverables: ["Strategic Assessment", "Capability Mapping", "Opportunity Analysis"]
  },
  {
    phase: 2,
    title: "Design & Planning",
    description: "Based on strategy, we design the projects that make your objectives tangible. Research and complete understanding of internal organization and external factors ensure execution success.",
    deliverables: ["Project Blueprint", "Resource Planning", "Risk Assessment"]
  },
  {
    phase: 3,
    title: "Build & Execute",
    description: "We design both the capabilities necessary for projects to succeed and the processes that must be followed. Every procedure is evaluated, stakeholders trained, and systems deployed.",
    deliverables: ["Process Implementation", "Team Training", "System Deployment"]
  },
  {
    phase: 4,
    title: "Operate & Evolve",
    description: "Continuous monitoring, optimization, and knowledge transfer ensure sustained success. Your organization gains full operational control with our ongoing support.",
    deliverables: ["Performance Monitoring", "Continuous Improvement", "Knowledge Transfer"]
  }
];

// Grid items for homepage bento
export const gridItems = [
  {
    id: 1,
    title: "Management is a technology. Versionable. Scalable. Shareable.",
    description: "Our core philosophy",
    className: "lg:col-span-3 md:col-span-6 md:row-span-4 lg:min-h-[60vh]",
    imgClassName: "w-full h-full",
    titleClassName: "justify-end",
    img: "b1.svg",
    spareImg: "",
  },
  {
    id: 2,
    title: "We work globally, adapting to your time zone and communication style",
    description: "",
    className: "lg:col-span-2 md:col-span-3 md:row-span-3",
    imgClassName: "",
    titleClassName: "justify-start",
    img: "",
    spareImg: "",
  },
  {
    id: 3,
    title: "Our methodology stack",
    description: "Strategy, Tactics, Processes",
    className: "lg:col-span-2 md:col-span-3 md:row-span-2",
    imgClassName: "",
    titleClassName: "justify-center",
    img: "",
    spareImg: "",
  },
  {
    id: 4,
    title: "15 years of methodology development. Battle-tested frameworks.",
    description: "",
    className: "lg:col-span-3 md:col-span-3 md:row-span-1",
    imgClassName: "",
    titleClassName: "justify-center md:max-w-full max-w-60 text-center",
    img: "b4.svg",
    spareImg: "",
  },
  {
    id: 5,
    title: "Internal-first validation: We are our own first customer",
    description: "The HLK Tech Lab Approach",
    className: "md:col-span-3 md:row-span-2",
    imgClassName: "absolute right-0 bottom-0 md:w-96 w-60",
    titleClassName: "justify-center md:justify-start lg:justify-center",
    img: "/b5.svg",
    spareImg: "/grid.svg",
  },
  {
    id: 6,
    title: "Ready to transform how your business operates?",
    description: "",
    className: "lg:col-span-2 md:col-span-3 md:row-span-2",
    imgClassName: "",
    titleClassName: "justify-center md:max-w-full max-w-60 text-center",
    img: "",
    spareImg: "",
  },
];

export const socialMedia = [
  {
    id: 1,
    img: "/git.svg",
    url: "#"
  },
  {
    id: 2,
    img: "/link.svg",
    url: "#"
  },
];
